import { useEffect, useMemo, useState } from 'react';

import { Tabs } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import {
  checkDisableInitiativesOptionMobileSite,
  checkEnableCompanySupport,
  checkEnableNetchexEmployeeNavbar,
} from 'utils/helpers/featureFlags';
import {
  checkDisciplinaryModuleEnabled,
  // checkDisciplinaryModuleEnabled,
  checkEngagementModuleEnabled,
  checkFeedbackModuleEnabled,
  checkGoalModuleEnabled,
  checkOneOnOneModuleEnabled,
  checkReviewModuleEnabled,
  checkTaskModuleEnabled,
} from 'utils/helpers/moduleAccess';

import {
  EngagementIconDualTone,
  EngagementIconHover,
  EngagementIconSelected,
  FeedbackIconDualTone,
  FeedbackIconHover,
  FeedbackIconSelected,
  GoalIconDualTone,
  GoalIconHover,
  GoalIconSelected,
  InitiativesIconDualTone,
  InitiativesIconHover,
  InitiativesIconSelected,
  OneOnOneIconDualTone,
  OneOnOneIconHover,
  OneOnOneIconSelected,
  ReviewsIconDualTone,
  ReviewsIconHover,
  ReviewsIconSelected,
  DisciplinaryActionsIconDualTone,
  DisciplinaryActionsIconSelected,
  DisciplinaryActionsIconHover,
  NetchexLogo,
} from 'assets/assetsV1/iconComponents';

import { FEATURE_KEYS, MODULE_KEYS_USER_PREFERENCE_PERSISTENCE, TEAM_TYPE } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import {
  getMatchesForCurrentRoute,
  getPersistenceKey,
  getQueryParams,
  goToContinuousFeedback,
  goToDisciplinaryActions,
  goToEngagement,
  goToReview,
} from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { routes } from 'constants/routes';

import { NavItem } from 'uiToolkitV1/NavBar';

// import InsightsDropdown, { getEnabledMenuItems } from './InsightsDropdown';
import { HeaderIconContainer } from './common/styles';

const Container = styled.div`
  flex: 2 1.5 25.5%;
  margin-top: 0.75rem;
  margin-left: 0.72rem;
  height: 100%;
  ${(props) => props.theme.breakpoints.up('xl')} {
    flex: 2 1.5 20%;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  height: 73px;
  padding-bottom: 4px;
  padding-top: 4px;
  overflow: auto;
  align-items: baseline;
`;

const expectedUrls = [
  '/review/audit',
  '/goals/list',
  '/initiative/audit',
  '/oneOnOne/audit',
  '/feedback',
  '/engagement',
  '/disciplinary-actions',
  // for review form and result page
  '/review',
];

function MobileSiteHeaderActions({ history, location }) {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const { moduleName } = useOrganisationSettings();
  const {
    isHomeMatch,
    isTeamMatch,
    isOneOnOneMatch,
    isGoalsSettingsMatch,
    isInitiativesSettingsMatch,
    isReviewMatch,
    isAnalyticsV0Match,
    isFeedbackMatch,
    isEngagementMatch,
    isDisciplinaryActionMatch,
  } = getMatchesForCurrentRoute(location.pathname, location);

  const { search: queryString } = location;
  const { isGoalsPage, isInitiativesPage } = getQueryParams(queryString);

  const [employeeTeams, setEmployeeTeams] = useState(loggedInEmployee.teams ?? []);

  const putPersonalTeamToFirstPosition = () => {
    let personalTeamIndex = null;
    const personalTeam = employeeTeams?.find((team, index) => {
      if (team?.teamType === TEAM_TYPE.personal) {
        personalTeamIndex = index;
        return team;
      }
    });

    if (personalTeam) {
      const teamsArray = [...employeeTeams];
      teamsArray.splice(personalTeamIndex, 1);
      teamsArray.splice(0, 0, personalTeam);
      setEmployeeTeams(teamsArray);
    }
  };

  useEffect(() => {
    putPersonalTeamToFirstPosition();
  }, [loggedInEmployee?.teams]);

  /* Grid State persistence Key */
  const persistenceKey = useMemo(() => {
    // eslint-disable-next-line no-shadow
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
    const tabId = 'Explore';
    const uniqueId = null;

    return getPersistenceKey({
      moduleName,
      viewId: '-12', // MY_OKRs is always -12
      tabId,
      uniqueId,
    });
  }, []);

  const goalIconClickHandler = async () => {
    history.push({
      pathname: routes.goals.list,
    });
  };

  const initiativesIconClickHandler = () => {
    history.push({
      pathname: routes.initiative.audit,
      // search: `?cohort=${cohortSearchParams}`
    });
  };

  useEffect(() => {
    if (checkEnableNetchexEmployeeNavbar()) {
      return;
    }
    const currentPath = location.pathname;
    const matchCurrentPath = (currentVal) => currentPath.includes(currentVal);
    if (!expectedUrls.some(matchCurrentPath)) {
      goToReview(history);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isInitiatesDisabledMobileSite = isMobileView() && checkDisableInitiativesOptionMobileSite();

  return (
    <Container>
      <Tabs>
        <IconsContainer reviewOption={loggedInEmployee.isFeatureEnabled(FEATURE_KEYS.REVIEW)}>
          {checkReviewModuleEnabled() && loggedInEmployee.isFeatureEnabled(FEATURE_KEYS.REVIEW) && (
            <HeaderIconContainer onClick={() => goToReview(history)}>
              <NavItem
                size="small"
                Icon={ReviewsIconDualTone}
                HoverIcon={ReviewsIconHover}
                ActiveIcon={ReviewsIconSelected}
                isActive={(isReviewMatch && !isAnalyticsV0Match) || isHomeMatch}
              >
                Reviews
              </NavItem>
            </HeaderIconContainer>
          )}
          {checkDisciplinaryModuleEnabled() && (
            <HeaderIconContainer onClick={() => goToDisciplinaryActions(history)}>
              <NavItem
                Icon={DisciplinaryActionsIconDualTone}
                HoverIcon={DisciplinaryActionsIconHover}
                ActiveIcon={DisciplinaryActionsIconSelected}
                isActive={isDisciplinaryActionMatch}
                height="56px"
              >
                Disciplinary actions
              </NavItem>
            </HeaderIconContainer>
          )}
          {checkGoalModuleEnabled() && (
            <HeaderIconContainer onClick={goalIconClickHandler}>
              <NavItem
                size="small"
                Icon={GoalIconDualTone}
                HoverIcon={GoalIconHover}
                ActiveIcon={GoalIconSelected}
                isActive={(isTeamMatch && isGoalsPage) || isGoalsSettingsMatch}
              >
                {modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}
              </NavItem>
            </HeaderIconContainer>
          )}

          {!isInitiatesDisabledMobileSite && checkTaskModuleEnabled() && (
            <HeaderIconContainer onClick={initiativesIconClickHandler}>
              <NavItem
                size="small"
                Icon={InitiativesIconDualTone}
                HoverIcon={InitiativesIconHover}
                ActiveIcon={InitiativesIconSelected}
                isActive={(isTeamMatch && isInitiativesPage) || isInitiativesSettingsMatch}
              >
                Initiatives
              </NavItem>
            </HeaderIconContainer>
          )}

          {checkOneOnOneModuleEnabled() && (
            <HeaderIconContainer onClick={() => history.push(routes.oneOnOne.audit)}>
              <NavItem
                size="small"
                Icon={OneOnOneIconDualTone}
                HoverIcon={OneOnOneIconHover}
                ActiveIcon={OneOnOneIconSelected}
                isActive={isOneOnOneMatch && !isAnalyticsV0Match}
              >
                1:1
              </NavItem>
            </HeaderIconContainer>
          )}

          {checkFeedbackModuleEnabled() && (
            <HeaderIconContainer onClick={() => goToContinuousFeedback(history)}>
              <NavItem
                size="small"
                Icon={FeedbackIconDualTone}
                HoverIcon={FeedbackIconHover}
                ActiveIcon={FeedbackIconSelected}
                isActive={isFeedbackMatch && !isAnalyticsV0Match}
              >
                Feedback
              </NavItem>
            </HeaderIconContainer>
          )}

          {checkEngagementModuleEnabled() && (
            <HeaderIconContainer onClick={() => goToEngagement(history)}>
              <NavItem
                size="small"
                Icon={EngagementIconDualTone}
                HoverIcon={EngagementIconHover}
                ActiveIcon={EngagementIconSelected}
                isActive={isEngagementMatch && !isAnalyticsV0Match}
              >
                Surveys
              </NavItem>
            </HeaderIconContainer>
          )}
        </IconsContainer>
      </Tabs>
    </Container>
  );
}

export default withRouter(MobileSiteHeaderActions);
